import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { useAtomValue, useSetAtom } from "jotai";
import { ArrowLeft, X } from "lucide-react";
import { motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { mintGuestAndAttachCustomer } from "@/api/auth/guest.utils";
import { merchantQueryOptions } from "@/api/vendure/merchant/merchant.query.options";
import { activeCartQueryOptions } from "@/api/vendure/order/order.query.options";
import { LoginSheet } from "@/components/business/auth/LoginSheet";
import { CheckoutCta } from "@/components/business/checkout/CheckoutCta";
import { CouponSection } from "@/components/business/checkout/coupon/CouponSection";
import { CartSummary } from "@/components/business/checkout/items/CartSummary";
import { OrderSummary } from "@/components/business/checkout/items/OrderSummary";
import { OrderTypeSection } from "@/components/business/checkout/order-type/OrderTypeSection";
import { PickupConfirmSheet } from "@/components/business/checkout/order-type/PickupConfirmSheet";
import { PaymentSection } from "@/components/business/checkout/payment/PaymentSection";
import { SecurePaymentBadge } from "@/components/business/checkout/SecurePaymentBadge";
import { OrderTypeSheet } from "@/components/business/order-type/OrderTypeSheet";
import { TableSpotSheet } from "@/components/business/order-type/TableSpotSheet";
import { CompactLoader, LoaderThree } from "@/components/ui/loader";
import { useActivateVenue } from "@/hooks/useActivateVenue";
import { useCartSync } from "@/hooks/useCartSync";
import { useCheckoutValidation } from "@/hooks/useCheckoutValidation";
import { usePlaceOrder } from "@/hooks/usePlaceOrder";
import { noindexHead } from "@/lib/seo";
import { currentUserAtom, guestDetailsAtom } from "@/stores/auth.store";
import { couponPendingAtom, orderAtom } from "@/stores/cart.store";
import {
  deliveryMethodAtom,
  loginSheetOpenAtom,
  venueIsOnlineAtom,
} from "@/stores/delivery.store";

export const Route = createFileRoute("/business/$slug_/checkout")({
  ssr: false,
  head: noindexHead,
  pendingComponent: () => (
    <div className="min-h-screen flex items-center justify-center">
      <CompactLoader />
    </div>
  ),
  component: Sentry.withProfiler(CheckoutPage, { name: "CheckoutPage" }),
});

const OUTCOME_MESSAGES = {
  pending: "business.checkout.payment_processing",
  incomplete: "business.checkout.payment_incomplete",
  error: "business.checkout.payment_error",
} as const;

export function CheckoutPage() {
  useActivateVenue();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { slug } = useParams({ strict: false }) as { slug: string };
  const { data: venues } = useQuery({
    ...merchantQueryOptions(slug),
    staleTime: 0,
  });
  const venue = venues?.[0];

  const setIsOnline = useSetAtom(venueIsOnlineAtom);
  const setLoginSheetOpen = useSetAtom(loginSheetOpenAtom);
  const setOrder = useSetAtom(orderAtom);
  const currentUser = useAtomValue(currentUserAtom);
  const guestDetails = useAtomValue(guestDetailsAtom);
  const deliveryMethod = useAtomValue(deliveryMethodAtom);
  const isGuestSession = !currentUser;

  const { data: activeCart } = useQuery(
    activeCartQueryOptions(venue?.token ?? "", !!venue?.token),
  );

  useEffect(() => {
    if (activeCart) setOrder(activeCart);
  }, [activeCart]);

  const { isSyncing, syncError } = useCartSync(venue?.token);
  const isCouponPending = useAtomValue(couponPendingAtom);

  const {
    isValid,
    isLoadingMethods,
    blockingReason,
    paymentMethods,
    selectedMethodId,
    setSelectedMethodId,
    hasAddress,
    hasTableSpot,
  } = useCheckoutValidation(venue?.token);

  const { handlePlaceOrder, isPlacing, cancelPlacing } = usePlaceOrder();
  const bottomRef = useRef<HTMLDivElement>(null);
  const [hasReviewed, setHasReviewed] = useState(false);
  const [pickupConfirmOpen, setPickupConfirmOpen] = useState(false);

  useEffect(() => {
    if (isLoadingMethods || !bottomRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setHasReviewed(true);
      },
      { threshold: 0.5 },
    );
    observer.observe(bottomRef.current);
    return () => observer.disconnect();
  }, [isLoadingMethods]);

  useEffect(() => {
    if (venue?.open_status?.is_open !== undefined) {
      setIsOnline(venue.open_status.is_open);
    }
  }, [venue?.open_status?.is_open, setIsOnline]);

  function handlePressPlaceOrder() {
    if (blockingReason === "needLogin") {
      setLoginSheetOpen(true);
      return;
    }

    if (deliveryMethod === "takeaway") {
      setPickupConfirmOpen(true);
      return;
    }

    placeOrder();
  }

  function handleConfirmPickup() {
    setPickupConfirmOpen(false);
    placeOrder();
  }

  async function placeOrder() {
    const method = paymentMethods.find((pm) => pm.id === selectedMethodId);
    if (!method || !venue?.token || isCouponPending) return;

    const outcome = await handlePlaceOrder(method.code, venue.token, slug);

    if (outcome.status === "success") {
      if (isGuestSession && guestDetails) {
        try {
          const data = await mintGuestAndAttachCustomer(venue.token, {
            firstName: guestDetails.firstName,
            lastName: guestDetails.lastName,
            emailAddress: guestDetails.email || undefined,
            countryCode: guestDetails.countryCode,
            phoneNumber: guestDetails.phoneNumber || undefined,
          });
          setOrder(data.cart);
        } catch (err) {
          Sentry.captureException(err);
        }
      }
      navigate({
        to: "/business/$slug/order-confirm",
        params: { slug },
        search: { code: outcome.order.code },
        replace: true,
      });
      return;
    }

    if (outcome.status !== "error" && outcome.status !== "incomplete") return;

    toast.error(
      outcome.status === "error" && outcome.message
        ? outcome.message
        : t(OUTCOME_MESSAGES[outcome.status] ?? OUTCOME_MESSAGES.error),
    );
  }

  const isLoading =
    isLoadingMethods || isPlacing || isSyncing || isCouponPending;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="min-h-screen bg-background"
    >
      <div className="sticky top-0 z-10 flex items-center gap-3 px-4 py-4 backdrop-blur-sm">
        <button
          type="button"
          onClick={() =>
            navigate({
              to: "/business/$slug/cart",
              params: { slug },
              replace: true,
            })
          }
          className="h-9 w-9 rounded-full bg-muted flex items-center justify-center shrink-0"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <h1 className="text-base font-bold text-foreground leading-tight">
            {t("business.checkout.title")}
          </h1>
          {venue?.name && (
            <p className="text-xs text-muted-foreground leading-tight">
              {venue.name}
            </p>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto w-full px-4 pb-6 space-y-2">
        <OrderTypeSection venue={venue} slug={slug} />

        {!hasAddress && (
          <div
            data-testid="checkout.validation.address"
            className="rounded-2xl bg-destructive/10 px-4 py-3"
          >
            <p className="text-xs text-destructive">
              {t("business.checkout.validation.needAddress")}
            </p>
          </div>
        )}

        {!hasTableSpot && (
          <div
            data-testid="checkout.validation.table"
            className="rounded-2xl bg-destructive/10 px-4 py-3"
          >
            <p className="text-xs text-destructive">
              {t("business.checkout.validation.needTableSpot")}
            </p>
          </div>
        )}

        {syncError && (
          <div
            data-testid="checkout.validation.sync"
            className="rounded-2xl bg-destructive/10 px-4 py-3"
          >
            <p className="text-xs text-destructive">{syncError}</p>
          </div>
        )}

        <PaymentSection
          methods={paymentMethods}
          selectedId={selectedMethodId}
          onSelect={setSelectedMethodId}
          isLoading={isLoadingMethods}
        />

        <CartSummary />
        <CouponSection channelToken={venue?.token} />
        <OrderSummary />
        <SecurePaymentBadge />
        <div ref={bottomRef} />
      </div>

      {venue && (
        <OrderTypeSheet
          deliveryMethods={venue.delivery_methods}
          schedule={venue.schedule}
          isOpen={venue.open_status?.is_open ?? false}
          slug={slug}
        />
      )}

      <TableSpotSheet />

      <PickupConfirmSheet
        open={pickupConfirmOpen}
        onClose={() => setPickupConfirmOpen(false)}
        onConfirm={handleConfirmPickup}
      />

      <LoginSheet />

      {isPlacing && (
        <div
          data-testid="checkout.placing-overlay"
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 backdrop-blur-[2px]"
        >
          <button
            type="button"
            aria-label={t("common.actions.close")}
            onClick={cancelPlacing}
            className="absolute top-4 right-4 p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <X size={32} />
          </button>
          <LoaderThree />
        </div>
      )}

      <CheckoutCta
        isValid={isValid}
        isLoading={isLoading}
        hasReviewed={hasReviewed}
        blockingReason={blockingReason}
        syncError={syncError}
        onPress={handlePressPlaceOrder}
      />
    </motion.div>
  );
}
